import { useState, useRef, useEffect } from "react";
import { Button, Input, Layout, Row, Col, Form } from "antd";
import { useNavigate } from "react-router-dom";
import { ShowToast, Severty } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import signinLogo from "../../assets/images/logo.svg";

const { Content } = Layout;

const OTPPage = () => {
  const navigate = useNavigate();
  const { request } = useRequest();

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30); // countdown seconds
  const [resending, setResending] = useState(false);

  const inputsRef = useRef([]);

  // Retrieve info from localStorage
  const email = localStorage.getItem("email");
  const mobileNumber = localStorage.getItem("mobileNumber");
  const countryCode = localStorage.getItem("countryCode");

  // 🕒 Countdown timer
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // 🧩 Handle OTP input
  const handleChange = (e, index) => {
    const value = e.target.value.replace(/\D/, ""); // only digits
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        inputsRef.current[index - 1].focus();
      }
    }
  };

  // ✅ Verify OTP
  const handleVerify = async () => {
    const enteredOtp = otp.join("").trim();

    if (enteredOtp.length !== 6) {
      ShowToast(Severty.ERROR, "Please enter a valid 6-digit OTP");
      return;
    }

    try {
      setLoading(true);
      await request({
        url: apiPath.verifyOTP, // Use verifyOTP API path
        method: "POST",
        data: {
          otp: enteredOtp,
          email: email, // Only email is needed for your API
        },
        onSuccess: (response) => {
          if (response?.status === 200) {
            ShowToast(
              Severty.SUCCESS,
              response?.message || "OTP verified successfully!"
            );

            // Clear the stored email from localStorage
            localStorage.removeItem("email");
            localStorage.removeItem("mobileNumber");
            localStorage.removeItem("countryCode");

            // Redirect to login page
            navigate("/login");
          } else {
            ShowToast(Severty.ERROR, response?.message || "Invalid OTP");
          }
        },
        onError: (error) => {
          const errorMessage =
            error.response?.data?.message || "OTP verification failed";
          ShowToast(Severty.ERROR, errorMessage);
        },
      });
    } catch (error) {
      ShowToast(Severty.ERROR, error.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // 🔁 Resend OTP function
  const handleResend = async () => {
    try {
      setResending(true);
      await request({
        url: apiPath.resendOTP, // Adjust this path as needed
        method: "POST",
        data: { email }, // Only email for your API
        onSuccess: (response) => {
          if (response?.status === 200) {
            ShowToast(
              Severty.SUCCESS,
              response?.message || "OTP resent successfully!"
            );
            setTimer(30); // restart timer
          } else {
            ShowToast(
              Severty.ERROR,
              response?.message || "Failed to resend OTP"
            );
          }
        },
        onError: (error) => {
          const errorMessage =
            error.response?.data?.message || "Failed to resend OTP";
          ShowToast(Severty.ERROR, errorMessage);
        },
      });
    } catch (error) {
      ShowToast(Severty.ERROR, "Something went wrong while resending OTP");
    } finally {
      setResending(false);
    }
  };

  // 📧 Dynamic message
  const getOtpMessage = () => {
    if (email) return `We sent a 6-digit code to ${email}`;
    if (mobileNumber && countryCode) {
      // mask mobile number for security
      const masked =
        mobileNumber.length >= 4
          ? `${countryCode} ${mobileNumber.slice(0, 2)}•••${mobileNumber.slice(
              -2
            )}`
          : `${countryCode} ${mobileNumber}`;
      return `We sent a 6-digit code to ${masked}`;
    }
    return "We sent a 6-digit code to your account";
  };

  return (
    <div className="main-new-login-page-designe-divv">
      <Layout className="layout-default layout-signin">
        <Content className="signin">
          <div className="contant-bg-sign">
            <div className="signin-box">
              <div className="signup-logo">
                <img src={signinLogo} alt="" />
              </div>
              <Row justify="space-around">
                <Col xs={{ span: 24 }} lg={{ span: 24 }} md={{ span: 24 }}>
                  <div className="signup-form">
                    <div className="login_header-mian">
                      <h4>Enter the code</h4>
                      <h5>{getOtpMessage()}</h5>
                    </div>

                    <Form
                      layout="vertical"
                      className="sign_in_formm"
                      onFinish={handleVerify}
                    >
                      <Form.Item>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "10px",
                            marginTop: "20px",
                          }}
                        >
                          {otp.map((digit, index) => (
                            <Input
                              key={index}
                              ref={(el) => (inputsRef.current[index] = el)}
                              value={digit}
                              onChange={(e) => handleChange(e, index)}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              maxLength={1}
                              style={{
                                width: 50,
                                height: 50,
                                textAlign: "center",
                                fontSize: 24,
                              }}
                            />
                          ))}
                        </div>
                      </Form.Item>

                      <Form.Item>
                        <Button
                          className="fill-button"
                          htmlType="submit"
                          loading={loading}
                          block
                        >
                          Verify
                        </Button>
                      </Form.Item>
                    </Form>

                    <div
                      className="already_have_account"
                      style={{ textAlign: "center" }}
                    >
                      {timer > 0 ? (
                        <>
                          Haven't received it yet? You can resend the code in{" "}
                          <span>{timer}s</span>
                        </>
                      ) : (
                        <Button
                          type="link"
                          onClick={handleResend}
                          loading={resending}
                          disabled={resending}
                        >
                          Resend Code
                        </Button>
                      )}
                    </div>

                    <div
                      className="already_have_account"
                      style={{ textAlign: "center", marginTop: "10px" }}
                    >
                      <span
                        style={{ color: "#1890ff", cursor: "pointer" }}
                        onClick={() => navigate("/login")}
                      >
                        Back to Login
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Content>
      </Layout>
    </div>
  );
};

export default OTPPage;
